import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, DatePicker, Spin, Empty, Tag, Row, Col, Statistic } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import ListPageHeader from '@shared/ListPageHeader';
import workforceStatsService from '@services/operator/workforceStatsService';

const { RangePicker } = DatePicker;

const STATION_COLORS = {
  picking: '#1890ff',
  packing: '#52c41a',
  inbound: '#faad14',
  returns: '#f5222d',
  stock_taking: '#722ed1',
};

const OperatorPerformance = () => {
  const { t } = useTranslation();
  const { id: operatorId } = useParams();
  const [dateRange, setDateRange] = useState([dayjs().subtract(14, 'day'), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!operatorId || !dateRange?.[0] || !dateRange?.[1]) return;
    setLoading(true);
    try {
      const result = await workforceStatsService.getOperatorPerformance(
        operatorId,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      if (result.success) setData(result.data);
    } catch (error) {
      console.error('[OperatorPerformance] Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [operatorId, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 計算匯總
  const totals = useMemo(() => {
    return (data?.days || []).reduce(
      (acc, day) => {
        day.stations.forEach((s) => {
          acc.tasks += s.tasks_completed || 0;
          acc.lines += s.lines_processed || 0;
          acc.items += s.items_processed || 0;
          acc.seconds += s.total_work_seconds || 0;
          acc.days_worked.add(day.date);
        });
        return acc;
      },
      { tasks: 0, lines: 0, items: 0, seconds: 0, days_worked: new Set() }
    );
  }, [data]);

  const totalHours = totals.seconds > 0 ? totals.seconds / 3600 : 0;

  // 圖表資料：每日 UPH + 件數趨勢（按站別拆分）
  const chartData = useMemo(() => {
    if (!data?.days) return [];

    // 找出所有出現過的站別
    const stationSet = new Set();
    data.days.forEach((day) => day.stations.forEach((s) => stationSet.add(s.station)));
    const stations = Array.from(stationSet);

    return data.days.map((day) => {
      const row = { date: day.date };
      stations.forEach((st) => {
        const stat = day.stations.find((s) => s.station === st);
        row[`uph_${st}`] = stat?.uph_items || 0;
        row[`items_${st}`] = stat?.items_processed || 0;
      });
      return row;
    });
  }, [data]);

  const activeStations = useMemo(() => {
    if (!data?.days) return [];
    const set = new Set();
    data.days.forEach((day) => day.stations.forEach((s) => set.add(s.station)));
    return Array.from(set);
  }, [data]);

  // 每日明細表格
  const columns = [
    {
      title: t('operator.workforce_stats.date'),
      dataIndex: 'date',
      key: 'date',
      width: 110,
      fixed: 'left',
    },
    {
      title: t('operator.workforce_stats.station'),
      key: 'station',
      width: 100,
      render: (_, record) =>
        record.stations.map((s) => (
          <Tag key={s.station} color={STATION_COLORS[s.station]}>
            {t(`operator.workforce_stats.stations.${s.station}`)}
          </Tag>
        )),
    },
    {
      title: t('operator.workforce_stats.tasks_completed'),
      key: 'tasks_completed',
      width: 80,
      render: (_, record) => sumField(record.stations, 'tasks_completed'),
    },
    {
      title: t('operator.workforce_stats.lines_processed'),
      key: 'lines_processed',
      width: 80,
      render: (_, record) => sumField(record.stations, 'lines_processed'),
    },
    {
      title: t('operator.workforce_stats.items_processed'),
      key: 'items_processed',
      width: 80,
      render: (_, record) => sumField(record.stations, 'items_processed'),
    },
    {
      title: t('operator.workforce_stats.work_hours'),
      key: 'work_hours',
      width: 80,
      render: (_, record) => {
        const seconds = sumField(record.stations, 'total_work_seconds');
        return seconds > 0 ? (seconds / 3600).toFixed(1) + 'h' : '-';
      },
    },
    {
      title: 'UPH',
      key: 'uph_items',
      width: 80,
      render: (_, record) => {
        const val = avgField(record.stations, 'uph_items');
        return val > 0 ? val.toFixed(1) : '-';
      },
    },
    {
      title: t('operator.workforce_stats.time_range'),
      key: 'time_range',
      width: 160,
      render: (_, record) => {
        const first = record.stations.map((s) => s.first_action_at).filter(Boolean).sort()[0];
        const last = record.stations.map((s) => s.last_action_at).filter(Boolean).sort().pop();
        if (!first || !last) return '-';
        return `${dayjs(first).format('HH:mm')} ~ ${dayjs(last).format('HH:mm')}`;
      },
    },
  ];

  return (
    <div>
      <ListPageHeader
        title={data?.operator_name || t('operator.workforce_stats.operator_performance_title')}
        breadcrumb={[
          { title: t('operator.workforce_stats.title'), href: '/workforce_stats' },
          { title: data?.operator_name || '...' },
        ]}
      />

      <div style={{ marginBottom: 16 }}>
        <RangePicker
          value={dateRange}
          onChange={(val) => val && setDateRange(val)}
          allowClear={false}
        />
      </div>

      <Spin spinning={loading}>
        {/* 匯總卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('operator.workforce_stats.total_days_worked')}
                value={totals.days_worked.size}
                suffix={t('operator.workforce_stats.days')}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('operator.workforce_stats.total_tasks')}
                value={totals.tasks}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('operator.workforce_stats.total_items')}
                value={totals.items}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('operator.workforce_stats.avg_uph_period')}
                value={totalHours > 0 ? (totals.items / totalHours).toFixed(1) : '-'}
              />
            </Card>
          </Col>
        </Row>

        {/* UPH 趨勢圖 */}
        {chartData.length > 0 && (
          <Card title={t('operator.workforce_stats.uph_trend')} style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                {activeStations.map((st) => (
                  <Line
                    key={st}
                    type="monotone"
                    dataKey={`uph_${st}`}
                    name={t(`operator.workforce_stats.stations.${st}`) + ' UPH'}
                    stroke={STATION_COLORS[st] || '#999'}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* 每日件數趨勢（堆疊柱狀圖） */}
        {chartData.length > 0 && (
          <Card title={t('operator.workforce_stats.items_trend')} style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                {activeStations.map((st) => (
                  <Bar
                    key={st}
                    dataKey={`items_${st}`}
                    name={t(`operator.workforce_stats.stations.${st}`)}
                    fill={STATION_COLORS[st] || '#999'}
                    stackId="items"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* 每日明細 */}
        <Card title={t('operator.workforce_stats.daily_detail')}>
          {data?.days?.length ? (
            <Table
              dataSource={data.days}
              columns={columns}
              rowKey="date"
              pagination={false}
              scroll={{ x: 700 }}
              size="small"
            />
          ) : (
            <Empty description={t('operator.workforce_stats.no_data')} />
          )}
        </Card>
      </Spin>
    </div>
  );
};

const sumField = (stations, field) =>
  (stations || []).reduce((sum, s) => sum + (s[field] || 0), 0);

const avgField = (stations, field) => {
  const values = (stations || []).map((s) => s[field] || 0).filter((v) => v > 0);
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

export default OperatorPerformance;
